import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatTL } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "Ev", full_name: "", phone: "", address: "", city: "", district: "", postal_code: "" });
  const [guestEmail, setGuestEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, price: i.price })),
        address: form,
        payment_method: "bank_transfer",
        notes: "",
        guest_email: user ? null : guestEmail,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success(t("checkout.success"));
      navigate(user ? `/account?tab=orders` : "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return <div className="px-6 py-32 text-center text-sm">{t("cart.empty")}</div>;
  }

  return (
    <div data-testid="checkout-page" className="px-6 lg:px-16 py-12 max-w-[1440px] mx-auto">
      <h1 className="font-heading text-4xl lg:text-5xl mb-10">{t("checkout.title")}</h1>
      {!user && (
        <div data-testid="guest-banner" className="mb-8 p-4 border border-[#C8A96A]/40 bg-[#C8A96A]/5 text-sm flex flex-wrap items-center justify-between gap-3">
          <span className="text-[#52525B]">
            {lang === "tr" ? "Misafir olarak sipariş veriyorsunuz." : "You are checking out as a guest."}
          </span>
          <Link to="/login" className="text-[#C8A96A] underline underline-offset-4 text-xs uppercase tracking-wider">
            {lang === "tr" ? "Giriş Yap" : "Sign In"}
          </Link>
        </div>
      )}
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_400px] gap-12">
        <div className="space-y-8">
          {!user && (
            <div>
              <h2 className="text-xs uppercase tracking-[0.2em] mb-6 font-medium">{lang === "tr" ? "İletişim" : "Contact"}</h2>
              <Input label={lang === "tr" ? "E-posta" : "Email"} value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} testid="guest-email" required type="email" />
            </div>
          )}
          <div>
            <h2 className="text-xs uppercase tracking-[0.2em] mb-6 font-medium">{t("checkout.address")}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label={t("checkout.addr_title")} value={form.title} onChange={onChange("title")} testid="addr-title" />
              <Input label={t("checkout.full_name")} value={form.full_name} onChange={onChange("full_name")} testid="addr-name" required />
              <Input label={t("checkout.phone")} value={form.phone} onChange={onChange("phone")} testid="addr-phone" required />
              <Input label={t("checkout.city")} value={form.city} onChange={onChange("city")} testid="addr-city" required />
              <Input label={t("checkout.district")} value={form.district} onChange={onChange("district")} testid="addr-district" required />
              <Input label={t("checkout.postal")} value={form.postal_code} onChange={onChange("postal_code")} testid="addr-postal" />
              <div className="sm:col-span-2">
                <Input label={t("checkout.addr_text")} value={form.address} onChange={onChange("address")} testid="addr-text" required />
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-xs uppercase tracking-[0.2em] mb-6 font-medium">{t("checkout.payment")}</h2>
            <label className="flex items-center gap-3 border border-[#0A0A0A] p-4 cursor-pointer">
              <input type="radio" name="pay" checked readOnly className="accent-[#C8A96A]" data-testid="payment-bank" />
              <span className="text-sm">{t("checkout.bank")}</span>
            </label>
            <p className="text-xs text-[#52525B] mt-2">{lang === "tr" ? "IBAN bilgileri sipariş onay e-postanızda paylaşılacaktır." : "IBAN details will be sent in your order confirmation email."}</p>
          </div>
        </div>
        <aside className="bg-[#F5F2EC] p-8 h-fit">
          <h2 className="font-heading text-2xl mb-6">{t("cart.title")}</h2>
          <div className="space-y-3 text-sm max-h-64 overflow-y-auto pr-2 mb-6">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between">
                <span className="truncate pr-4">{lang === "tr" ? i.name_tr : i.name_en} × {i.quantity}</span>
                <span className="font-finance shrink-0">{formatTL(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#0A0A0A]/20 pt-4 flex justify-between text-lg">
            <span className="font-medium">{t("cart.total")}</span>
            <span className="font-finance">{formatTL(subtotal)}</span>
          </div>
          <button data-testid="place-order-btn" disabled={submitting} className="mt-8 w-full bg-[#0A0A0A] hover:bg-[#C8A96A] text-white py-4 text-xs uppercase tracking-[0.25em] transition-colors disabled:opacity-50">
            {submitting ? t("common.loading") : t("checkout.place_order")}
          </button>
        </aside>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, testid, required, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] mb-2 text-[#52525B]">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} required={required}
        className="w-full border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none focus:border-[#C8A96A]" />
    </div>
  );
}
